<template>
  <v-row no-gutters style="padding: 0 15px">
    <v-col v-if="is_fetching">
      <v-row>
        <v-col align="center">
          <v-skeleton-loader type="heading"></v-skeleton-loader>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="4">
          <v-skeleton-loader type="paragraph"></v-skeleton-loader>
        </v-col>
      </v-row>
      <v-row>
        <v-col align="center">
          <v-skeleton-loader type="heading" width="500"></v-skeleton-loader>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="4">
          <v-skeleton-loader type="text" width="250"></v-skeleton-loader>
          <v-skeleton-loader type="text@8"></v-skeleton-loader>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-skeleton-loader type=" table-thead, table-tbody"></v-skeleton-loader>
        </v-col>
      </v-row>
    </v-col>
    <v-col v-else-if="!is_report_available">
      <v-alert text prominent type="error" icon="mdi-cloud-alert">
        {{ $t("common.labels.noReportAvailable") }}
      </v-alert>
    </v-col>
    <div id="document" ref="document" align="start" v-else-if="report_data && is_report_available && !is_fetching">
      <v-col cols="12" class="d-flex justify-center">
        <v-menu
          v-model="menu"
          :close-on-content-click="false"
          :nudge-right="40"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="date"
              prepend-inner-icon="mdi-calendar-outline"
              v-bind="attrs"
              v-on="on"
              readonly
              filled
              rounded
              :label="`Please select week period`"
            />
          </template>
          <v-date-picker
            v-if="allowedDates.length > 0"
            v-model="week"
            @input="get_week($event)"
            :allowed-dates="get_allowed_dates"
            :min="get_date_limit(allowedDates).min"
            :max="get_date_limit(allowedDates).max"
            :events="reportStartDates"
            event-color="green lighten-1"
            multiple
            show-adjacent-months
          ></v-date-picker>
        </v-menu>
      </v-col>

      <ScavengerReportHeading
        v-if="device_details"
        :platform="device_details.DeviceID"
        :tenant="tenant"
        :report_date="report_date"
        :report_time="report_time"
        :final_recepient="final_recepient"
        :recipients_number="recipients_number"
        report_period="weekly"
        :report_beginning_date="report_beginning_date"
      />
      <v-fab-transition
        ><v-btn
          :class="`${is_scavenger && show_alerts ? 'mr-12' : ''}`"
          color="cautionBackground"
          @click="exportToPDF"
          large
          fixed
          bottom
          right
          fab
        >
          <v-icon color="cautionText">mdi-file-export</v-icon>
        </v-btn>
      </v-fab-transition>
      <v-col cols="12" class="ml-3">
        <v-row no-gutters>
          <v-col cols="12" class="mb-2">
            <v-row no-gutters class="mb-2">
              <v-col cols="12" class="text-subtitle-1">Transformer Details</v-col>
            </v-row>
            <v-row no-gutters class="mb-4" v-if="device_details">
              <v-col cols="4">Transformer ID</v-col>
              <v-col cols="8">{{ device_details.DeviceID }}</v-col>
              <v-col cols="4">Transformer Location</v-col>
              <v-col cols="8">Dublin</v-col>
              <v-col cols="4">Transformer Coordinates</v-col>
              <v-col cols="8">{{ device_coordinates }}</v-col>
              <v-col cols="4">Transformer Rating</v-col>
              <v-col cols="8">{{ device_details.TransformerRating }} kVa</v-col>
              <v-col cols="4">Feeders Connected</v-col>
              <v-col cols="8">{{ getActiveFeeders(device_details.ActiverFeeders) }}</v-col>
            </v-row>
          </v-col>
          <v-col cols="12" class="mb-6" id="first_page" ref="first_page">
            <v-row no-gutters>
              <v-col cols="12" class="text-subtitle-1">Compliance Summary</v-col>
            </v-row>
            <v-row no-gutters v-for="(header, idx) in config.sections" :key="`${header.id}_${idx}`">
              <v-col cols="4">{{ header.label }}</v-col>
              <v-col cols="8">
                <ChipsVT :status="get_summary_key(report_data.compliance_summary[`${header.id}`])" />
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col cols="4">Harmonic Voltages</v-col>
              <v-col cols="8">
                <div v-for="phase in config.phases" :key="phase">
                  <ChipsVT
                    v-if="isHarmonicViolationExist(phase)"
                    status="failed"
                    :label="
                      `${phase.toUpperCase()}: H` +
                      report_data.compliance_summary.harmonics.perc_95[`${phase}`].join(', H')
                    "
                  />
                  <ChipsVT v-else status="pass" :label="`${phase.toUpperCase()}: Pass`" />
                </div>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col cols="12" class="text-subtitle-1">Total Power Quality Events</v-col>
            </v-row>
            <v-row
              no-gutters
              v-for="(header, idx) in events.pq_issue_distrubution.data"
              :key="`${header.value}_${idx}`"
            >
              <v-col v-if="report_data.totalFaults && report_data.totalFaults[header.value]" cols="4">{{
                header.label
              }}</v-col>
              <v-col v-if="report_data.totalFaults && report_data.totalFaults[header.value]" cols="8">
                <ChipsVT
                  status="info"
                  :label="report_data.totalFaults[`${header.value}`]"
                  :tooltip="`Number of ${header.label}`"
                />
              </v-col>
            </v-row>
          </v-col>

          <v-col
            cols="12"
            class="mb-6"
            v-for="(header, indx) in config.en50160_report"
            :key="`${header.label}_${indx}`"
          >
            <v-row no-gutters>
              <v-col cols="12" class="text-subtitle-1">{{ header.label }}</v-col>
            </v-row>
            <v-row no-gutters>
              <v-col>
                <v-row no-gutters class="text-caption font-weight-bold primaryGrey--text">
                  <v-col cols="3">Requirement</v-col>
                  <v-col cols="2">Measured Values</v-col>
                  <v-col cols="2">Available Margin</v-col>
                  <v-col cols="1">Result</v-col>
                </v-row>
              </v-col>
              <v-col cols="12" v-for="(data, id) in header.data" :key="`${data.perc}_${id}_${indx}`">
                <v-row v-if="data.lines" no-gutters class="text-caption">
                  <v-col align-self="center" cols="3" class="font-weight-bold"
                    >{{ data.perc }}% of week: {{ data.min }} ~
                    {{ data.max }}
                  </v-col>
                  <v-col cols="4">
                    <v-row no-gutters v-for="(line, i) in data.lines" :key="`${line}_${i}_${indx}`">
                      <v-col cols="6">
                        {{ line }} :
                        {{
                          report_data[header.key][id] && report_data[header.key][id][i]
                            ? `${report_data[header.key][id][i].mean.toFixed(2)}`
                            : "-"
                        }}
                      </v-col>
                      <v-col cols="6"
                        >{{
                          report_data[header.key][id] && report_data[header.key][id][i].margin
                            ? `% ${report_data[header.key][id][i].margin}`
                            : "-"
                        }}
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-col
                    v-if="report_data[header.key][id] && report_data[header.key][id][i]"
                    align-self="center"
                    cols="1"
                    ><ChipsVT :status="getComplianceStatus(header.key, data.perc)" />
                  </v-col>
                  <v-col align-self="center" cols="1" v-else>-</v-col>
                </v-row>
                <v-row v-else no-gutters class="text-caption">
                  <v-col cols="3" class="font-weight-bold"
                    >{{ data.perc }}% of week: {{ data.min }} ~ {{ data.max }}
                  </v-col>
                  <v-col cols="2">
                    {{
                      report_data[header.key][id][i] ? `${report_data[header.key][id][i].mean.toFixed(2)}` : "-"
                    }}</v-col
                  >
                  <v-col cols="2">
                    {{
                      report_data[header.key][id] && report_data[header.key][id][i].margin
                        ? `% ${report_data[header.key][id][i].margin}`
                        : "-"
                    }}</v-col
                  >
                  <v-col cols="1">
                    <ChipsVT :status="getComplianceStatus(header.key, data.perc)" />
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12">
            <v-row no-gutters>
              <v-col cols="12" class="mb-6" v-for="(header, indx) in config.phases" :key="`${header.label}_${indx}`">
                <v-row class="text-center add-border primaryGrey--text" no-gutters>
                  <v-col cols="12"> {{ `Voltage Harmonics - Phase ${header.toUpperCase()}` }}</v-col>
                </v-row>
                <v-row class="text-center add-border primaryGrey--text" no-gutters>
                  <v-col cols="8">Odd Harmonics</v-col>
                  <v-col cols="4">Even Harmonics</v-col>
                </v-row>
                <v-row class="text-center add-border primaryGrey--text" no-gutters>
                  <v-col cols="4">Not Multiples of 3</v-col>
                  <v-col cols="4">Multiples of 3</v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="4" v-for="(data, idx) in config.harmonics_category" :key="`cat_${idx}`">
                    <v-row class="text-center text-caption add-border font-weight-bold primaryGrey--text" no-gutters>
                      <v-col cols="2">H</v-col>
                      <v-col cols="2">Value</v-col>
                      <v-col cols="2">Limit</v-col>
                      <v-col cols="3">Margin %</v-col>
                      <v-col cols="3">Message</v-col>
                    </v-row>
                    <v-row
                      v-for="(h, i) in data"
                      :key="`cat__val_${i}`"
                      class="text-center text-caption add-border"
                      no-gutters
                    >
                      <v-col cols="2">H{{ h }}</v-col>
                      <v-col cols="2">{{ report_data.harmonics.values.perc_95[header][h - 2] }}</v-col>
                      <v-col cols="2">{{ config.harmonics_limit[h - 2] }}%</v-col>
                      <v-col cols="3">{{ report_data.harmonics.margin_available.perc_95[header][h - 2] }}</v-col>
                      <v-col cols="3">
                        <ChipsVT :status="report_data.harmonics.message.perc_95[header][h - 2]" />
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
    </div>
  </v-row>
</template>

<script>
import { axios_get } from "../services/vt_service";
import { scv } from "../services/powerdata";
import { tracking } from "../services/tracking";
import { content } from "../../config/assets.js";
import { content as reports_config } from "../../config/scavenger_reports.js";
import { content as scavenger_events } from "../../config/scavenger.js";
import ChipsVT from "./ChipsVT.vue";
import ScavengerReportHeading from "./ScavengerReportHeading.vue";
import html2pdf from "html2pdf.js";
import moment from "moment";
import { scavenger_grpc_services } from "../services/ScavengerServices";

const tenant_key = process.env["VUE_APP_TENANT_KEY"];

export default {
  name: "ScavengerEN50160Report",
  components: { ChipsVT, ScavengerReportHeading },
  props: {
    device_id: String,
  },
  data: function () {
    return {
      report_data: null,
      device_details: null,
      tenant: null,
      recepient: null,
      report_date: null,
      report_beginning_date: null,
      report_time: null,
      snackbar: false,
      network_error: false,
      error_message: null,
      recipients_list: null,
      config: null,
      i: 0, // for blocking the console error
      report_start_date: null,
      report_end_date: null,
      date: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substr(0, 10),
      menu: false,
      week: null,
      available_dates: null,
      minDate: null,
      maxDate: null,
      allowedDates: [],
      week_number: null,
      year: null,
      reportStartDates: [],
      is_fetching: true,
      platform: null,
      events: null,
    };
  },
  created() {
    this.page_content = content;
    this.config = reports_config;
    this.events = scavenger_events;
  },
  mounted() {
    this.recepient = this.$auth.user.email;
    this.tenant = this.$auth.user[tenant_key]["tenant"];
    this.platform = this.tenant == "h2scan" ? "Sentinel" : "VT Electron";

    this.getDeviceDetails();
    this.getScavengerAvailableReportDates();

    if (this.$route.name == "Report_Snapshot") this.getRecipientsList();
    // this.$store.commit("update_page_title", "Report");
  },
  watch: {
    week_number: function (newVal, oldVal) {
      if (this.year && newVal !== oldVal) {
        this.report_data = null;
        this.getEn50160Report();
      }
    },
  },
  methods: {
    async getScavengerAvailableReportDates() {
      this.network_error = false;

      await scavenger_grpc_services.GetScavengerAvailableReportDates(this.device_id, "en50160").then((response) => {
        this.is_fetching = false;
        this.available_dates = response;
        this.available_dates.length === 0 ? (this.is_report_available = false) : (this.is_report_available = true);
        if (this.is_report_available) {
          this.week_number = this.available_dates[0].week;
          this.year = this.available_dates[0].year;
          this.available_dates.map((week) => {
            if (week.availableDates) {
              this.allowedDates.push(...week.availableDates);
              this.reportStartDates.push(week.availableDates[0]);
            }
          });
        }
      });

      // await axios_get(scv.get_scavenger_available_report_dates, {
      //   id: this.device_id,
      //   type: "en50160",
      // }).then((response) => {
      //   this.is_fetching = false;
      //   this.available_dates = response;
      //   this.available_dates.length === 0 ? (this.is_report_available = false) : (this.is_report_available = true);
      //   if (this.is_report_available) {
      //     this.week_number = this.available_dates[0].week;
      //     this.year = this.available_dates[0].year;
      //     this.available_dates.map((week) => {
      //       if (week.availableDates) {
      //         this.allowedDates.push(...week.availableDates);
      //         this.reportStartDates.push(week.availableDates[0]);
      //       }
      //     });
      //   }
      // });
    },
    async getEn50160Report() {
      this.network_error = false;
      await axios_get(scv.get_en50160_report, {
        id: this.device_id,
        type: "en50160",
        year: this.year,
        week: this.week_number,
      }).then((response) => {
        if (response.length > 0) {
          this.report_data = response[0];
          this.report_date = this.$formatDateFormat(
            this.report_data.reportDates[this.report_data.reportDates.length - 1]
          );
          this.report_beginning_date = this.$formatDateFormat(this.report_data.reportDates[0]);
          this.report_time = this.$formatDate(this.report_data.reportGeneratedAt);
        }
      });
      // await scavenger_grpc_services
      //   .GetEn50160Report(this.device_id, "en50160", this.year, this.week_number)
      //   .then((response) => {
      //     if (response.length > 0) {
      //       this.report_data = response[0];
      //       this.report_date = this.$formatDateFormat(
      //         this.report_data.reportDates[this.report_data.reportDates.length - 1]
      //       );
      //       this.report_beginning_date = this.$formatDateFormat(this.report_data.reportDates[0]);
      //       this.report_time = this.$formatDate(this.report_data.reportGeneratedAt);
      //     }
      //   });
    },
    get_summary_key(obj) {
      const keys = Object.keys(obj);
      const firstKey = keys[0];
      return obj[firstKey] ? "pass" : "failed";
    },
    get_date_limit(availableDates) {
      const dateObjects = availableDates.map((dateString) => new Date(dateString));

      const minDate = new Date(Math.min(...dateObjects));
      const maxDate = new Date(Math.max(...dateObjects));

      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const day = date.getDate().toString().padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      return {
        min: formatDate(minDate),
        max: formatDate(maxDate),
      };
    },
    get_allowed_dates: function (val) {
      return this.allowedDates.indexOf(val) !== -1;
    },
    get_week(date) {
      const startDate = new Date(date[date.length - 1]);
      const startOfWeek = new Date(startDate);
      startOfWeek.setDate(startDate.getDate() - startDate.getDay());

      const daysOfWeek = [];
      for (let i = 0; i < 7; i++) {
        const currentDate = new Date(startOfWeek);
        currentDate.setDate(startOfWeek.getDate() + i);

        const day = currentDate.getDate().toString().padStart(2, "0");
        const month = (currentDate.getMonth() + 1).toString().padStart(2, "0");
        const year = currentDate.getFullYear();

        const formattedDate = `${year}-${month}-${day}`;
        daysOfWeek.push(formattedDate);
      }
      this.week = daysOfWeek;
      this.week_number = this.getWeekNumber(startDate);
      this.year = startDate.getFullYear();
    },
    getWeekNumber(date) {
      return moment(date).week();
    },
    getComplianceStatus(key, perc) {
      let status = this.report_data.compliance_summary[key][`perc_${perc}`];
      return status ? "pass" : "failed";
    },
    isHarmonicViolationExist(phase) {
      // Below condition is to check if there is any violation in the harmonics if so return as true
      if (
        this.report_data.compliance_summary.harmonics.perc_95[`${phase}`] &&
        this.report_data.compliance_summary.harmonics.perc_95[`${phase}`].length > 0
      ) {
        return true;
      }
      return false;
    },
    async getDeviceDetails() {
      this.network_error = false;
      // await axios_get(scv.get_scavenger_data, {
      //   id: this.device_id,
      // }).then((response) => {
      //   this.device_details = response[0];
      // });
      await scavenger_grpc_services.GetScavengerData(this.device_id).then((response) => {
        this.device_details = response[0];
      });
    },
    async getRecipientsList() {
      this.network_error = false;
      const response = await axios_get(tracking.get_scavenger_data, {
        id: 1,
      });
      this.recipients_list = response[0];
    },
    getActiveFeeders(active_feeders) {
      return active_feeders.toString().split("1").length - 1;
    },
    exportToPDF() {
      var opt = {
        margin: [0.4, 0, 0.7, 0],
        filename: "report_" + new Date().toString() + ".pdf",
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scrollX: 0,
          scrollY: 0,
          dpi: 190,
          letterRendering: true,
        },
        jsPDF: { unit: "in", format: "a4", orientation: "p" },
        pagebreak: {
          mode: ["css", "legacy"],
        },
      };
      html2pdf()
        .from(this.$refs.document)
        .set(opt)
        .toPdf()
        .get("pdf")
        .then(function () {
          //Customize the PDF here
        })
        .save();
      this.$store.commit("hide_loader");
    },
  },
  computed: {
    final_recepient: function () {
      if (this.$route.name == "Report_Snapshot") {
        if (this.recipients_list && this.recipients_list.length > 0) {
          let recepients = "";
          let total = this.recipients_list.length;
          let limit = total > 7 ? 7 : total;
          for (var i = 0; i < limit; i++) {
            recepients = recepients
              ? recepients + "; " + this.recipients_list[i]["RecipientEmail"]
              : this.recipients_list[i]["RecipientEmail"];
          }
          return recepients;
        }
        return this.recepient;
      }
      return this.recepient;
    },
    recipients_number: function () {
      if (this.$route.name == "Report_Snapshot") {
        if (this.recipients_list && this.recipients_list.length > 0) {
          let total = this.recipients_list.length;
          return total > 7 ? total - 7 : null;
        }
        return null;
      }
      return null;
    },
    device_coordinates: function () {
      if (this.device_details && this.device_details.CurrentLat && this.device_details.CurrentLng) {
        return `${this.device_details.CurrentLat}, ${this.device_details.CurrentLng}`;
      }
      return "-";
    },
    is_scavenger: function () {
      return this.$store.state.tenant_config
        ? this.$store.state.tenant_config.powerdata_type == "scavenger" ||
            this.$store.state.tenant_config.powerdata_type == "h2scan"
        : false;
    },
    show_alerts: function () {
      return (
        this.$store.state.tenant_config.show_alerts_section &&
        ("alerts_enabled" in this.$store.state.user_config ? this.$store.state.user_config.alerts_enabled : true)
      );
    },
  },
};
</script>

<style lang="scss">
.add-border {
  border-bottom: 1px solid var(--v-restingOutline-base);
}

.add-border div {
  border-right: 1px solid var(--v-restingOutline-base);
}

.add-border div:last-of-type {
  border-right: none;
}
</style>
